import { useSyncExternalStore } from 'react';

export const AI_MOTION_MS = 240;
export const AI_MOTION_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
export const AI_MOTION_SETTLE_SLACK_MS = 60;

export const motionTransition = (
  properties: string[],
  reducedMotion: boolean
) =>
  reducedMotion
    ? 'none'
    : properties
        .map((property) => `${property} ${AI_MOTION_MS}ms ${AI_MOTION_EASING}`)
        .join(', ');

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const subscribeReducedMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};

const readReducedMotion = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;

export const usePrefersReducedMotion = () =>
  useSyncExternalStore(subscribeReducedMotion, readReducedMotion);

export interface PanelTransitionState {
  wanted: boolean;
  mounted: boolean;
  visible: boolean;
}

export type PanelTransitionAction =
  | { type: 'show'; reducedMotion: boolean }
  | { type: 'hide'; reducedMotion: boolean }
  | { type: 'frame' }
  | { type: 'exitEnd' };

export const panelTransitionInitial = (
  wanted: boolean
): PanelTransitionState => ({ wanted, mounted: wanted, visible: wanted });

export const panelTransitionReducer = (
  state: PanelTransitionState,
  action: PanelTransitionAction
): PanelTransitionState => {
  switch (action.type) {
    case 'show':
      if (action.reducedMotion) {
        return { wanted: true, mounted: true, visible: true };
      }
      return state.mounted
        ? { wanted: true, mounted: true, visible: true }
        : { wanted: true, mounted: true, visible: false };
    case 'hide':
      if (action.reducedMotion || !state.visible) {
        return { wanted: false, mounted: false, visible: false };
      }
      return { wanted: false, mounted: true, visible: false };
    case 'frame':
      return state.wanted && state.mounted && !state.visible
        ? { ...state, visible: true }
        : state;
    case 'exitEnd':
      return !state.wanted && state.mounted
        ? { wanted: false, mounted: false, visible: false }
        : state;
    default:
      return state;
  }
};
